import { useState, useEffect, useRef } from "react";

const API = process.env.REACT_APP_API_URL;

const STATUS_CONFIG = {
  0: { label: "Bueno",   bg: "#dcfce7", color: "#15803d", dot: "#22c55e" },  // ≥80
  1: { label: "Regular", bg: "#fef9c3", color: "#854d0e", dot: "#eab308" },  // 40–79
  2: { label: "Crítico", bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },  // ≤39
};

const ESTADO_COLORS = {
  "Certificado":  "#22c55e",
  "Pendiente":    "#3b82f6",
  "Sin entrega":  "#f97316",
  "Reprogramado": "#a78bfa",
};

function scoreColor(s) {
  if (s == null) return "#94a3b8";
  if (s >= 80) return "#22c55e";   // Verde: 80-100
  if (s >= 40) return "#eab308";   // Amarillo: 40-79
  return "#ef4444";                // Rojo: 0-39
}

// Sparkline 7 días
// vals[0] = día más antiguo (Dia_7), vals[last] = día más reciente (Dia_1)
// Tendencia: compara primer mitad vs segunda mitad para ver si mejora o empeora
function Sparkline({ scores }) {
  const vals = (scores || []).filter(v => v != null && !isNaN(v));
  if (vals.length === 0) return <span style={{ color: "#94a3b8", fontSize: 11 }}>—</span>;
  if (vals.length === 1) return (
    <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor(vals[0]) }}>{vals[0]}</span>
  );

  const first = vals[0];                          // valor más antiguo
  const last  = vals[vals.length - 1];            // valor más reciente
  const delta = last - first;                     // positivo = mejoró, negativo = empeoró

  // Color de la línea: refleja tendencia (verde=sube, rojo=baja, gris=estable)
  const trendColor = delta > 2 ? "#22c55e" : delta < -2 ? "#ef4444" : "#94a3b8";

  const min = Math.min(...vals), max = Math.max(...vals);
  const H = 22, W = 56, range = max - min || 1;
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * W;
    const y = H - ((v - min) / range) * (H - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const [lx, ly] = pts.split(" ").pop().split(",");

  // Flecha de tendencia
  const arrow = delta > 2 ? "▲" : delta < -2 ? "▼" : "→";
  const arrowColor = delta > 2 ? "#22c55e" : delta < -2 ? "#ef4444" : "#94a3b8";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <svg width={W} height={H} style={{ overflow: "visible" }}>
        {/* Área bajo la curva */}
        <polyline
          points={pts + ` ${W},${H} 0,${H}`}
          fill={trendColor} fillOpacity={0.08}
          stroke="none"
        />
        {/* Línea */}
        <polyline points={pts} fill="none" stroke={trendColor} strokeWidth="1.5" strokeLinejoin="round" />
        {/* Punto final (más reciente) */}
        <circle cx={parseFloat(lx)} cy={parseFloat(ly)} r="2.5" fill={trendColor} />
      </svg>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor(last) }}>{Math.round(last)}</span>
        <span style={{ fontSize: 9, fontWeight: 700, color: arrowColor, lineHeight: 1 }}>
          {arrow}{Math.abs(delta) > 0 ? Math.abs(delta) : ""}
        </span>
      </div>
    </div>
  );
}

// ── Celda de Tendencia ─────────────────────────────────────────
function TendenciaCell({ scoreRef, labelRef, prom10d, fechaInicio, fechaFin, diasReales }) {
  if (prom10d == null) return <span style={{ color: "#94a3b8", fontSize: 11 }}>Sin datos</span>;

  const fmtFecha = (f) => {
    if (!f) return "—";
    const [y, m, d] = String(f).slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  };

  const delta   = scoreRef != null ? Math.round(prom10d - scoreRef) : null;
  const desmejoro = prom10d < 80;
  const mejoro    = !desmejoro && delta != null && delta > 0;
  const estable   = !desmejoro && !mejoro;

  const badgeBg    = mejoro ? "#dcfce7" : desmejoro ? "#fee2e2" : "#f1f5f9";
  const badgeColor = mejoro ? "#15803d" : desmejoro ? "#991b1b" : "#64748b";
  const badgeIcon  = mejoro ? "▲" : desmejoro ? "▼" : "→";
  const badgeTxt   = mejoro ? "Mejoró" : desmejoro ? "Desmejoró" : "Estable";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 170 }}>
      {/* Badge resultado final */}
      {(
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 3,
          background: badgeBg, color: badgeColor,
          fontSize: 10, fontWeight: 700, padding: "2px 7px",
          borderRadius: 10, width: "fit-content",
        }}>
          {badgeIcon} {badgeTxt}
        </span>
      )}
      {/* Comparación principal */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
        {scoreRef != null && (
          <>
            <span style={{ color: "#94a3b8", fontSize: 10 }}>{labelRef}</span>
            <span style={{ fontWeight: 800, color: scoreColor(scoreRef), fontSize: 14 }}>{Math.round(scoreRef)}</span>
            <span style={{ color: "#94a3b8" }}>→</span>
          </>
        )}
        <span style={{ color: "#94a3b8", fontSize: 10 }}>Últ. {diasReales ?? 15}d</span>
        <span style={{ fontWeight: 800, color: scoreColor(prom10d), fontSize: 14 }}>{Math.round(prom10d)}</span>
      </div>
      {/* Rango de fechas */}
      {fechaInicio && fechaFin && (
        <span style={{ fontSize: 9, color: "#94a3b8" }}>
          {fmtFecha(fechaInicio)} – {fmtFecha(fechaFin)}
        </span>
      )}
    </div>
  );
}

// Comparación antes → después con flecha
function ComparacionScore({ antes, despues, cortesAntes, cortesDespues }) {
  if (antes == null && despues == null) return <span style={{ color: "#94a3b8", fontSize: 11 }}>Sin cert.</span>;
  if (antes == null) return (
    <div style={{ fontSize: 11, color: "#64748b" }}>
      <span style={{ fontWeight: 700, color: scoreColor(despues) }}>{Math.round(despues)}</span>
      <span style={{ color: "#94a3b8" }}> (solo post)</span>
    </div>
  );
  if (despues == null) return (
    <div style={{ fontSize: 11, color: "#64748b" }}>
      <span style={{ fontWeight: 700, color: scoreColor(antes) }}>{Math.round(antes)}</span>
      <span style={{ color: "#94a3b8" }}> (solo pre)</span>
    </div>
  );
  const delta = Math.round(despues - antes);
  const mejoro = despues > antes;
  const igual  = despues === antes;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: scoreColor(antes) }}>{Math.round(antes)}</span>
        <span style={{ fontSize: 13 }}>{mejoro ? "→" : igual ? "→" : "→"}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor(despues) }}>{Math.round(despues)}</span>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 8,
          background: mejoro ? "#dcfce7" : igual ? "#f1f5f9" : "#fee2e2",
          color: mejoro ? "#15803d" : igual ? "#64748b" : "#991b1b",
        }}>
          {mejoro ? "▲" : igual ? "=" : "▼"}{Math.abs(delta) > 0 ? Math.abs(delta) : ""}
        </span>
      </div>
      <div style={{ fontSize: 9, color: "#94a3b8" }}>
        {cortesAntes} corte{cortesAntes !== 1 ? "s" : ""} pre · {cortesDespues} post
      </div>
    </div>
  );
}

// KPI card
function KpiCard({ label, value, sub, bg, color }) {
  const display = (value == null || !isFinite(value) || isNaN(value)) ? "—" : value;
  return (
    <div style={{ background: bg, borderRadius: 12, padding: "14px 18px", minWidth: 110, boxShadow: "0 1px 6px rgba(0,0,0,0.07)", flex: 1 }}>
      <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1 }}>{display}</div>
      <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color, marginTop: 2, fontWeight: 600 }}>{sub}</div>}
    </div>
  );
}

export default function CalidadNodosHuella({ usuario }) {
  const [fechasCargadas, setFechasCargadas] = useState([]);
  const [cargandoUp, setCargandoUp]         = useState(false);
  const [msgUp, setMsgUp]                   = useState(null);
  const fileRef = useRef(null);

  const [reporte, setReporte]         = useState([]);
  const [kpis, setKpis]               = useState(null);
  const [cargandoRep, setCargandoRep] = useState(false);

  const [filtroBusqueda, setFiltroBusqueda] = useState("");
  const [filtroAliado, setFiltroAliado]     = useState("");
  const [filtroJefe, setFiltroJefe]         = useState("");
  const [filtroEstado, setFiltroEstado]     = useState("");
  const [filtroStatus, setFiltroStatus]     = useState("");
  const [filtroMesPrio, setFiltroMesPrio]   = useState("");
  const [filtroMesCert, setFiltroMesCert]   = useState("");
  const [ordenCol, setOrdenCol]             = useState("score_promedio");
  const [ordenDir, setOrdenDir]             = useState("asc");
  const [pagina, setPagina]                 = useState(1);
  const [vistaActiva, setVistaActiva]       = useState("tabla");
  const POR_PAGINA = 100;

  const esAdmin = (usuario?.rol || "").toLowerCase() === "administrador";

  const aliados = [...new Set(reporte.map(r => r.aliado).filter(Boolean))].sort();
  const jefes   = [...new Set(reporte.map(r => r.jefe_zona).filter(Boolean))].sort();
  const distEstado = reporte.reduce((acc, r) => {
    const e = r.estado_priorizacion || "Sin estado";
    acc[e] = (acc[e] || 0) + 1;
    return acc;
  }, {});

  // Meses únicos de priorización y certificación para los filtros
  const mesesPrio = [...new Set(
    reporte.map(r => r.mes_priorizacion).filter(Boolean)
  )].sort().reverse();
  const mesesCert = [...new Set(
    reporte.map(r => r.fecha_certificacion).filter(Boolean).map(f => f.slice(0, 7))
  )].sort().reverse();

  // Helper para mostrar mes como "Ene 2026"
  const mesMostrarCorto = (ym) => {
    if (!ym) return "—";
    const [y, m] = ym.split("-");
    const names = ["","Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
    return `${names[parseInt(m)] || m} ${y}`;
  };

  const cargarFechas = () => {
    fetch(`${API}/calidad-huella-fechas`)
      .then(r => r.json())
      .then(d => setFechasCargadas(d.fechas || []))
      .catch(() => {});
  };

  const cargarReporte = () => {
    setCargandoRep(true);
    const params = new URLSearchParams({ rol: usuario?.rol || "", cedula: usuario?.cedula || "" });
    fetch(`${API}/calidad-huella-reporte?${params}`)
      .then(r => r.json())
      .then(d => { setReporte(d.nodos || []); setKpis(d.kpis || null); setPagina(1); })
      .catch(() => {})
      .finally(() => setCargandoRep(false));
  };

  useEffect(() => { cargarFechas(); cargarReporte(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setCargandoUp(true); setMsgUp(null);
    const form = new FormData();
    form.append("archivo", file);
    try {
      const res  = await fetch(`${API}/calidad-huella-cargar`, { method: "POST", body: form });
      const data = await res.json();
      setMsgUp({ tipo: data.ok ? "ok" : "warn", texto: data.mensaje });
      if (data.ok) { cargarFechas(); cargarReporte(); }
    } catch { setMsgUp({ tipo: "err", texto: "❌ No se pudo conectar con el servidor." }); }
    finally   { setCargandoUp(false); }
  };

  const datosFiltrados = reporte
    .filter(row => {
      const q = filtroBusqueda.toLowerCase();
      return (
        (!q || row.nodo?.toLowerCase().includes(q) || row.nombre_nodo?.toLowerCase().includes(q) || row.ciudad?.toLowerCase().includes(q)) &&
        (!filtroAliado   || row.aliado === filtroAliado) &&
        (!filtroJefe     || row.jefe_zona === filtroJefe) &&
        (!filtroEstado   || row.estado_priorizacion === filtroEstado) &&
        (filtroStatus === "" || String(row.score_status) === filtroStatus) &&
        (!filtroMesPrio  || (row.mes_priorizacion || "").startsWith(filtroMesPrio)) &&
        (!filtroMesCert  || (row.fecha_certificacion || "").startsWith(filtroMesCert))
      );
    })
    .sort((a, b) => {
      const va = a[ordenCol] ?? "";
      const vb = b[ordenCol] ?? "";
      // Si ambos son números → comparación numérica
      if (typeof va === "number" && typeof vb === "number") {
        return ordenDir === "asc" ? va - vb : vb - va;
      }
      // Cualquier otro caso → string seguro (evita localeCompare sobre no-string)
      const sa = String(va);
      const sb = String(vb);
      return ordenDir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });

  const totalPags  = Math.ceil(datosFiltrados.length / POR_PAGINA);
  const datosPag   = datosFiltrados.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  const toggleOrden = (col) => {
    if (ordenCol === col) setOrdenDir(d => d === "asc" ? "desc" : "asc");
    else { setOrdenCol(col); setOrdenDir("asc"); }
    setPagina(1);
  };

  // ── Descarga CSV: filas = nodos, columnas = fechas únicas ──────
  const [descargando, setDescargando] = useState(false);

  const descargarExcel = async () => {
    try {
      setDescargando(true);
      const params = new URLSearchParams({ rol: usuario?.rol || "", cedula: usuario?.cedula || "" });
      const res  = await fetch(`${API}/calidad-huella-descarga?${params}`);
      const data = await res.json();
      const { nodoMeta, nodoFechas } = data;

      // Recolectar todas las fechas únicas y ordenarlas cronológicamente
      const todasFechas = [...new Set(
        Object.values(nodoFechas).flatMap(mapa => Object.keys(mapa))
      )].sort();

      // Construir cabecera
      const cabecera = [
        "Nodo", "Nombre Nodo", "Aliado", "Ciudad", "Jefe Zona",
        "Estado Huella", "Fecha Certificación",
        ...todasFechas
      ];

      // Construir filas — nodos ordenados alfabéticamente
      const filas = Object.keys(nodoMeta).sort().map(nodo => {
        const meta      = nodoMeta[nodo];
        const mapFechas = nodoFechas[nodo] || {};
        return [
          meta.nodo,
          meta.nombre_nodo,
          meta.aliado,
          meta.ciudad,
          meta.jefe_zona,
          meta.estado_priorizacion,
          meta.fecha_certificacion,
          ...todasFechas.map(f => mapFechas[f] != null ? mapFechas[f] : "")
        ];
      });

      // Generar CSV con BOM UTF-8 para que Excel abra correctamente tildes y ñ
      const bom        = "\uFEFF";
      const csvContent = bom + [cabecera, ...filas]
        .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(";"))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `CalidadNodosHuella_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("❌ Error al descargar: " + err.message);
    } finally {
      setDescargando(false);
    }
  };

  // Gráfico de barras top 20 peores
  const top20Peores = [...reporte].filter(r => r.score_promedio != null)
    .sort((a, b) => a.score_promedio - b.score_promedio).slice(0, 20);
  const CHART_H = 220;
  const CHART_W = Math.max(600, top20Peores.length * 40);

  const msgColorMap = { ok: ["#dcfce7","#15803d"], warn: ["#fef9c3","#854d0e"], err: ["#fee2e2","#991b1b"] };

  const Th = ({ col, label, right }) => (
    <th onClick={() => toggleOrden(col)}
      style={{ ...thStyle, cursor: "pointer", textAlign: right ? "right" : "left" }}>
      {label} {ordenCol === col ? (ordenDir === "asc" ? "▲" : "▼") : <span style={{ opacity: 0.25 }}>⇅</span>}
    </th>
  );

  return (
    <div style={{ padding: "24px 28px", fontFamily: "'Segoe UI', Arial, sans-serif", maxWidth: 1600, margin: "0 auto" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0f172a" }}>📡 Calidad Nodos Huella</h1>
          <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: 13 }}>
            Score QoE de los nodos priorizados · Regional 3
            {fechasCargadas.length > 0 && (
              <span style={{ marginLeft: 8, background: "#eff6ff", color: "#1d4ed8", padding: "1px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                {fechasCargadas.length} corte{fechasCargadas.length > 1 ? "s" : ""} · último: {fechasCargadas[0]}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 8, padding: 3, gap: 2 }}>
            {[["tabla","📋 Tabla"],["graficos","📊 Gráficos"]].map(([v,l]) => (
              <button key={v} onClick={() => setVistaActiva(v)} style={{
                padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer",
                fontSize: 12, fontWeight: 600,
                background: vistaActiva === v ? "white" : "transparent",
                color: vistaActiva === v ? "#0f172a" : "#64748b",
                boxShadow: vistaActiva === v ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
              }}>{l}</button>
            ))}
          </div>
          {/* Botón descarga CSV: nodos en filas, fechas en columnas */}
          <button
            onClick={descargarExcel}
            disabled={descargando || reporte.length === 0}
            style={{
              padding: "7px 16px", borderRadius: 8,
              border: "1px solid #22c55e",
              background: descargando ? "#f0fdf4" : "#f0fdf4",
              color: descargando ? "#86efac" : "#15803d",
              cursor: (descargando || reporte.length === 0) ? "not-allowed" : "pointer",
              fontSize: 13, fontWeight: 600,
              display: "flex", alignItems: "center", gap: 6,
              opacity: reporte.length === 0 ? 0.5 : 1,
            }}
          >
            {descargando ? "⏳ Generando..." : "📥 Descargar CSV"}
          </button>
          {esAdmin && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={handleUpload} />
              <button onClick={() => fileRef.current?.click()} disabled={cargandoUp} style={{
                padding: "8px 16px", borderRadius: 8, border: "none", cursor: cargandoUp ? "not-allowed" : "pointer",
                background: cargandoUp ? "#94a3b8" : "linear-gradient(135deg, #E30613, #B0000E)",
                color: "white", fontSize: 13, fontWeight: 600,
                boxShadow: cargandoUp ? "none" : "0 2px 8px rgba(227,6,19,0.3)",
              }}>
                {cargandoUp ? "⏳ Cargando..." : "📤 Cargar nuevo corte"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Mensaje upload ── */}
      {msgUp && (
        <div style={{ padding: "10px 16px", borderRadius: 8, marginBottom: 16,
          background: (msgColorMap[msgUp.tipo]||msgColorMap.err)[0],
          color: (msgColorMap[msgUp.tipo]||msgColorMap.err)[1],
          fontSize: 13, fontWeight: 500, display: "flex", justifyContent: "space-between" }}>
          <span>{msgUp.texto}</span>
          <button onClick={() => setMsgUp(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, opacity: 0.6 }}>✕</button>
        </div>
      )}

      {/* ── Sin datos ── */}
      {!cargandoRep && reporte.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#94a3b8" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📂</div>
          <p style={{ fontSize: 15, margin: 0, fontWeight: 600 }}>No hay datos cargados aún.</p>
          {esAdmin && <p style={{ fontSize: 13, marginTop: 6 }}>Usa <strong>Cargar nuevo corte</strong> para subir el primer archivo.</p>}
        </div>
      )}

      {/* ── KPIs — calculados desde los datos filtrados para reflejar siempre el filtro activo ── */}
      {reporte.length > 0 && (() => {
        const base = datosFiltrados;
        const totalPriorizados    = base.length;
        const conScore            = base.filter(r => r.score_status != null).length;
        const sinScore            = base.filter(r => r.score_status == null).length;
        const buenos              = base.filter(r => r.score_status === 0).length;
        const regulares           = base.filter(r => r.score_status === 1).length;
        const criticos            = base.filter(r => r.score_status === 2).length;
        const scoresProm          = base.filter(r => r.score_promedio != null && r.score_promedio > 0).map(r => r.score_promedio);
        const scorePromedioGeneral = scoresProm.length > 0
          ? Math.round(scoresProm.reduce((s, v) => s + v, 0) / scoresProm.length)
          : null;
        return (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
            <KpiCard label="Nodos priorizados"    value={totalPriorizados}      bg="#f1f5f9" color="#0f172a" />
            <KpiCard label="Con score QoE"        value={conScore}              bg="#eff6ff" color="#1d4ed8"
              sub={totalPriorizados > 0 ? `${Math.round(conScore/totalPriorizados*100)}% cobertura` : null} />
            <KpiCard label="🟢 Buenos (≥80)"      value={buenos}                bg="#dcfce7" color="#15803d" />
            <KpiCard label="🟡 Regulares (40-79)" value={regulares}             bg="#fef9c3" color="#854d0e" />
            <KpiCard label="🔴 Críticos (<40)"    value={criticos}              bg="#fee2e2" color="#991b1b" />
            <KpiCard label="Sin score"            value={sinScore}              bg="#f8fafc" color="#64748b" />
          </div>
        );
      })()}

      {cargandoRep ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div><p>Cargando reporte...</p>
        </div>
      ) : reporte.length > 0 && (
        <>
          {/* ══ VISTA GRÁFICOS ══ */}
          {vistaActiva === "graficos" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              {/* Gráfico antes/después para certificados */}
              {reporte.some(r => r.score_prom_antes != null && r.score_prom_despues != null) && (
                <div style={{ background: "white", borderRadius: 12, padding: "20px 24px", boxShadow: "0 2px 10px rgba(0,0,0,0.07)" }}>
                  <h3 style={{ margin: "0 0 4px 0", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                    📈 Comparación Score QoE: Antes vs Después de Certificación
                  </h3>
                  <p style={{ margin: "0 0 16px 0", fontSize: 12, color: "#64748b" }}>
                    Solo nodos certificados con datos en ambos períodos · ordenados por mayor mejora
                  </p>
                  <div style={{ overflowX: "auto" }}>
                    {(() => {
                      const certificados = reporte
                        .filter(r => r.score_prom_antes != null && r.score_prom_despues != null)
                        .sort((a, b) => (b.score_prom_despues - b.score_prom_antes) - (a.score_prom_despues - a.score_prom_antes))
                        .slice(0, 20);
                      const W2 = Math.max(500, certificados.length * 52);
                      return (
                        <svg width={W2} height={CHART_H + 60} style={{ display: "block" }}>
                          {[0,40,80,100].map(v => {
                            const y = CHART_H - (v/100)*CHART_H;
                            return (
                              <g key={v}>
                                <line x1={0} y1={y} x2={W2} y2={y} stroke={v===40||v===80?"#fbbf24":"#f1f5f9"}
                                  strokeDasharray={v===40||v===80?"4,3":"0"} strokeWidth={v===40||v===80?1.5:1} />
                                <text x={-4} y={y+4} textAnchor="end" fontSize={9} fill="#94a3b8">{v}</text>
                              </g>
                            );
                          })}
                          {certificados.map((row, i) => {
                            const x     = i * 52 + 4;
                            const bW    = 18;
                            const hA    = (row.score_prom_antes / 100) * CHART_H;
                            const hD    = (row.score_prom_despues / 100) * CHART_H;
                            const mejoro = row.score_prom_despues >= row.score_prom_antes;
                            return (
                              <g key={row.nodo}>
                                {/* Barra ANTES */}
                                <rect x={x} y={CHART_H - hA} width={bW} height={hA} rx={2}
                                  fill={scoreColor(row.score_prom_antes)} opacity={0.45} />
                                <text x={x + bW/2} y={CHART_H - hA - 3} textAnchor="middle" fontSize={8} fill={scoreColor(row.score_prom_antes)}>
                                  {row.score_prom_antes}
                                </text>
                                {/* Barra DESPUÉS */}
                                <rect x={x + bW + 2} y={CHART_H - hD} width={bW} height={hD} rx={2}
                                  fill={scoreColor(row.score_prom_despues)} opacity={0.9} />
                                <text x={x + bW + 2 + bW/2} y={CHART_H - hD - 3} textAnchor="middle" fontSize={8} fontWeight="700" fill={scoreColor(row.score_prom_despues)}>
                                  {row.score_prom_despues}
                                </text>
                                {/* Delta */}
                                <text x={x + bW + 1} y={CHART_H + 10} textAnchor="middle" fontSize={8} fontWeight="700"
                                  fill={mejoro ? "#15803d" : "#991b1b"}>
                                  {mejoro ? "▲" : "▼"}{Math.abs(row.score_prom_despues - row.score_prom_antes).toFixed(1)}
                                </text>
                                {/* Nombre nodo */}
                                <text x={x + bW + 1} y={CHART_H + 22} textAnchor="middle" fontSize={9} fill="#64748b"
                                  transform={`rotate(-40, ${x+bW+1}, ${CHART_H+22})`}>
                                  {row.nodo}
                                </text>
                              </g>
                            );
                          })}
                        </svg>
                      );
                    })()}
                  </div>
                  <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 11, color: "#64748b" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <div style={{ width: 12, height: 12, background: "#94a3b8", opacity: 0.45, borderRadius: 2 }} /> Antes
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <div style={{ width: 12, height: 12, background: "#22c55e", borderRadius: 2 }} /> Después
                    </span>
                  </div>
                </div>
              )}

              {/* Top 20 peores score promedio */}
              <div style={{ background: "white", borderRadius: 12, padding: "20px 24px", boxShadow: "0 2px 10px rgba(0,0,0,0.07)" }}>
                <h3 style={{ margin: "0 0 4px 0", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>🔴 Top 20 nodos con peor Score QoE promedio</h3>
                <p style={{ margin: "0 0 16px 0", fontSize: 12, color: "#64748b" }}>Promedio de todos los cortes cargados</p>
                <div style={{ overflowX: "auto" }}>
                  <svg width={CHART_W} height={CHART_H + 60} style={{ display: "block" }}>
                    {[0,40,80,100].map(v => {
                      const y = CHART_H - (v/100)*CHART_H;
                      return (
                        <g key={v}>
                          <line x1={0} y1={y} x2={CHART_W} y2={y} stroke={v===40||v===80?"#fbbf24":"#f1f5f9"}
                            strokeDasharray={v===40||v===80?"4,3":"0"} strokeWidth={v===40||v===80?1.5:1} />
                          <text x={-4} y={y+4} textAnchor="end" fontSize={9} fill="#94a3b8">{v}</text>
                        </g>
                      );
                    })}
                    {top20Peores.map((row, i) => {
                      const bW = 28, x = i*40+4;
                      const h  = (row.score_promedio/100)*CHART_H;
                      const col = scoreColor(row.score_promedio);
                      return (
                        <g key={row.nodo}>
                          <rect x={x} y={CHART_H-h} width={bW} height={h} rx={3} fill={col} opacity={0.85} />
                          <text x={x+bW/2} y={CHART_H-h-4} textAnchor="middle" fontSize={9} fontWeight="700" fill={col}>{row.score_promedio}</text>
                          <text x={x+bW/2} y={CHART_H+14} textAnchor="middle" fontSize={9} fill="#64748b"
                            transform={`rotate(-35, ${x+bW/2}, ${CHART_H+14})`}>{row.nodo}</text>
                        </g>
                      );
                    })}
                  </svg>
                </div>
              </div>

              {/* Score por aliado + estado priorización */}
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div style={{ background: "white", borderRadius: 12, padding: "20px 24px", boxShadow: "0 2px 10px rgba(0,0,0,0.07)", flex: 1, minWidth: 260 }}>
                  <h3 style={{ margin: "0 0 16px 0", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>🏢 Score promedio por aliado</h3>
                  {aliados.map(al => {
                    const nds = reporte.filter(r => r.aliado === al && r.score_promedio != null);
                    if (!nds.length) return null;
                    const prom = Math.round(nds.reduce((s,r) => s + r.score_promedio, 0) / nds.length);
                    const crit = nds.filter(r => r.score_status === 2).length;
                    return (
                      <div key={al} style={{ marginBottom: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                          <span style={{ fontSize: 12, color: "#334155", fontWeight: 600 }}>{al}</span>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {crit > 0 && <span style={{ fontSize: 11, color: "#ef4444", fontWeight: 700 }}>🔴 {crit}</span>}
                            <span style={{ fontSize: 13, fontWeight: 800, color: scoreColor(prom) }}>{prom}</span>
                          </div>
                        </div>
                        <div style={{ height: 8, background: "#f1f5f9", borderRadius: 4 }}>
                          <div style={{ width: `${prom}%`, height: "100%", background: scoreColor(prom), borderRadius: 4 }} />
                        </div>
                        <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{nds.length} nodos</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ background: "white", borderRadius: 12, padding: "20px 24px", boxShadow: "0 2px 10px rgba(0,0,0,0.07)", flex: 1, minWidth: 260 }}>
                  <h3 style={{ margin: "0 0 16px 0", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>📋 Estado de priorización</h3>
                  {Object.entries(distEstado).map(([estado, cnt]) => {
                    const pct = Math.round((cnt / reporte.length) * 100);
                    const col = ESTADO_COLORS[estado] || "#94a3b8";
                    return (
                      <div key={estado} style={{ marginBottom: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                          <span style={{ fontSize: 12, color: "#334155", fontWeight: 500 }}>{estado}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: col }}>{cnt} <span style={{ color: "#94a3b8", fontWeight: 400 }}>({pct}%)</span></span>
                        </div>
                        <div style={{ height: 6, background: "#f1f5f9", borderRadius: 3 }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: col, borderRadius: 3 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ══ VISTA TABLA ══ */}
          {vistaActiva === "tabla" && (
            <>
              {/* Filtros */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
                <input placeholder="🔍 Buscar nodo, nombre o ciudad..."
                  value={filtroBusqueda} onChange={e => { setFiltroBusqueda(e.target.value); setPagina(1); }}
                  style={inputStyle} />
                <select value={filtroStatus} onChange={e => { setFiltroStatus(e.target.value); setPagina(1); }} style={selectStyle}>
                  <option value="">Todos los scores</option>
                  <option value="0">🟢 Bueno (≥80)</option>
                  <option value="1">🟡 Regular (40-79)</option>
                  <option value="2">🔴 Crítico (&lt;40)</option>
                </select>
                {aliados.length > 1 && (
                  <select value={filtroAliado} onChange={e => { setFiltroAliado(e.target.value); setPagina(1); }} style={selectStyle}>
                    <option value="">Todos los aliados</option>
                    {aliados.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                )}
                {jefes.length > 1 && (
                  <select value={filtroJefe} onChange={e => { setFiltroJefe(e.target.value); setPagina(1); }} style={selectStyle}>
                    <option value="">Todos los jefes</option>
                    {jefes.map(j => <option key={j} value={j}>{j}</option>)}
                  </select>
                )}
                <select value={filtroEstado} onChange={e => { setFiltroEstado(e.target.value); setPagina(1); }} style={selectStyle}>
                  <option value="">Todos los estados</option>
                  {Object.keys(distEstado).map(e => <option key={e} value={e}>{e}</option>)}
                </select>
                {mesesPrio.length > 0 && (
                  <select value={filtroMesPrio} onChange={e => { setFiltroMesPrio(e.target.value); setPagina(1); }}
                    style={{ ...selectStyle, border: filtroMesPrio ? "1px solid #E30613" : "1px solid #e2e8f0" }}>
                    <option value="">📅 Mes priorización</option>
                    {mesesPrio.map(m => <option key={m} value={m}>{mesMostrarCorto(m)}</option>)}
                  </select>
                )}
                {mesesCert.length > 0 && (
                  <select value={filtroMesCert} onChange={e => { setFiltroMesCert(e.target.value); setPagina(1); }}
                    style={{ ...selectStyle, border: filtroMesCert ? "1px solid #16a34a" : "1px solid #e2e8f0" }}>
                    <option value="">✅ Mes certificación</option>
                    {mesesCert.map(m => <option key={m} value={m}>{mesMostrarCorto(m)}</option>)}
                  </select>
                )}
                {(filtroBusqueda||filtroStatus||filtroAliado||filtroJefe||filtroEstado||filtroMesPrio||filtroMesCert) && (
                  <button onClick={() => { setFiltroBusqueda(""); setFiltroStatus(""); setFiltroAliado(""); setFiltroJefe(""); setFiltroEstado(""); setFiltroMesPrio(""); setFiltroMesCert(""); setPagina(1); }}
                    style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #e2e8f0", background: "white", color: "#64748b", cursor: "pointer", fontSize: 12 }}>
                    ✕ Limpiar
                  </button>
                )}
                <span style={{ fontSize: 12, color: "#94a3b8", marginLeft: 4 }}>{datosFiltrados.length.toLocaleString()} nodos</span>
              </div>

              {/* Tabla */}
              <div style={{ overflowX: "auto", borderRadius: 10, boxShadow: "0 2px 10px rgba(0,0,0,0.07)" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, background: "white" }}>
                  <thead>
                    <tr style={{ background: "#0f172a" }}>
                      <Th col="nodo"                label="Nodo" />
                      <Th col="nombre_nodo"         label="Nombre nodo" />
                      <Th col="aliado"              label="Aliado" />
                      <Th col="ciudad"              label="Ciudad" />
                      <Th col="jefe_zona"           label="Jefe Zona" />
                      <Th col="estado_priorizacion" label="Estado Huella" />
                      <Th col="fecha_certificacion" label="Fecha Cert." />
                      {/* Columna antes/después */}
                      <th style={{ ...thStyle, whiteSpace: "nowrap", minWidth: 160 }}>
                        Antes → Después cert.
                      </th>
                      <th style={{ ...thStyle, minWidth: 150 }}>Tendencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datosPag.map((row, i) => {
                      const sc = row.score_status != null ? STATUS_CONFIG[row.score_status] : null;
                      const estadoColor = ESTADO_COLORS[row.estado_priorizacion] || "#94a3b8";
                      const scores7 = [row.score_dia_7,row.score_dia_6,row.score_dia_5,row.score_dia_4,row.score_dia_3,row.score_dia_2,row.score_dia_1].filter(v => v!=null);
                      const fechaInicio = row.score_dia_7 != null ? row.fecha_dia_7 : row.score_dia_6 != null ? row.fecha_dia_6 : row.fecha_dia_5 || null;
                      const fechaFin    = row.fecha_dia_1 || null;
                      // Para tendencia: score de referencia = cert. si tiene, sino priorización
                      const scoreRef  = row.tend_score_ref;
                      const labelRef  = row.fecha_certificacion ? "Cert." : "Prio.";
                      const prom10d   = row.tend_prom_10d;
                      const tFechaIni = row.tend_fecha_inicio;
                      const tFechaFin = row.tend_fecha_fin;
                      const diasReales = row.tend_dias_reales;
                      return (
                        <tr key={row.nodo+i}
                          style={{ background: i%2===0?"white":"#f8fafc", borderBottom: "1px solid #f1f5f9" }}
                          onMouseEnter={e => e.currentTarget.style.background="#eff6ff"}
                          onMouseLeave={e => e.currentTarget.style.background=i%2===0?"white":"#f8fafc"}>
                          <td style={tdStyle}><strong style={{ fontFamily: "monospace", color: "#0f172a" }}>{row.nodo}</strong></td>
                          <td style={{ ...tdStyle, color: "#334155", maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.nombre_nodo}>{row.nombre_nodo||"—"}</td>
                          <td style={{ ...tdStyle, fontSize: 11, color: "#64748b" }}>{row.aliado||"—"}</td>
                          <td style={{ ...tdStyle, fontSize: 11, color: "#64748b" }}>{row.ciudad||"—"}</td>
                          <td style={{ ...tdStyle, fontSize: 11, color: "#64748b", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.jefe_zona}>{row.jefe_zona||"—"}</td>
                          <td style={tdStyle}>
                            <span style={{ display:"inline-flex", alignItems:"center", gap:4, background:estadoColor+"18", color:estadoColor, padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:600, whiteSpace:"nowrap" }}>
                              {row.estado_priorizacion||"—"}
                            </span>
                          </td>
                          {/* Fecha certificación completa */}
                          <td style={{ ...tdStyle, fontSize: 11, fontWeight: 600, color: row.fecha_certificacion ? "#0f172a" : "#94a3b8", whiteSpace: "nowrap" }}>
                            {row.fecha_certificacion || "Sin cert."}
                          </td>
                          {/* Antes → Después */}
                          <td style={{ ...tdStyle, minWidth: 160 }}>
                            <ComparacionScore
                              antes={row.score_prom_antes}
                              despues={row.score_prom_despues}
                              cortesAntes={row.cortes_antes}
                              cortesDespues={row.cortes_despues}
                            />
                          </td>
                          <td style={{ ...tdStyle, paddingTop: 10, paddingBottom: 10 }}>
                            <TendenciaCell
                              scoreRef={scoreRef}
                              labelRef={labelRef}
                              prom10d={prom10d}
                              fechaInicio={tFechaIni}
                              fechaFin={tFechaFin}
                              diasReales={diasReales}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              {totalPags > 1 && (
                <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:6, marginTop:16, flexWrap:"wrap" }}>
                  <button onClick={()=>setPagina(1)}         disabled={pagina===1}         style={btnPage(pagina===1)}>«</button>
                  <button onClick={()=>setPagina(p=>p-1)}    disabled={pagina===1}         style={btnPage(pagina===1)}>‹</button>
                  {Array.from({length:Math.min(7,totalPags)},(_,i)=>{
                    const p=totalPags<=7?i+1:pagina<=4?i+1:pagina>=totalPags-3?totalPags-6+i:pagina-3+i;
                    return <button key={p} onClick={()=>setPagina(p)}
                      style={{...btnPage(false),background:p===pagina?"#E30613":"white",color:p===pagina?"white":"#0f172a",fontWeight:p===pagina?700:400}}>{p}</button>;
                  })}
                  <button onClick={()=>setPagina(p=>p+1)}    disabled={pagina===totalPags} style={btnPage(pagina===totalPags)}>›</button>
                  <button onClick={()=>setPagina(totalPags)} disabled={pagina===totalPags} style={btnPage(pagina===totalPags)}>»</button>
                  <span style={{fontSize:12,color:"#64748b"}}>Pág. {pagina}/{totalPags} · {datosFiltrados.length.toLocaleString()} nodos</span>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

const thStyle    = { padding:"10px 12px", textAlign:"left", fontSize:11, fontWeight:600, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.05em", whiteSpace:"nowrap", userSelect:"none" };
const tdStyle    = { padding:"8px 12px", verticalAlign:"middle" };
const selectStyle= { padding:"7px 12px", borderRadius:8, border:"1px solid #e2e8f0", fontSize:13, background:"white", color:"#0f172a", cursor:"pointer", outline:"none" };
const inputStyle = { padding:"7px 12px", borderRadius:8, border:"1px solid #e2e8f0", fontSize:13, outline:"none", minWidth:240 };
const btnPage    = (d) => ({ padding:"4px 10px", borderRadius:6, border:"1px solid #e2e8f0", background:"white", color:d?"#cbd5e1":"#0f172a", cursor:d?"not-allowed":"pointer", fontSize:12 });